/**
 * Buscador por capacidad: «¿qué rol le tengo que dar a alguien que necesita facturar?».
 *
 * No busca por nombre de rol —para eso está la lista— sino por lo que la persona
 * tiene que poder hacer. La consulta se expande con sinónimos del oficio y se
 * confronta contra las capacidades REALES que devolvió el endpoint: si el
 * despliegue no tiene módulo de facturación, buscar «facturar» no devuelve nada.
 * Nunca se fabrica una coincidencia.
 */

import type { Role, RoleCapability } from '@/panel/contract';
import { capacidadesDe, concedeAlgo } from '@/panel/contract';
import { etiquetaDeArea, expandirBusqueda, frasePermitida, normalizarTexto } from '@/panel/vocabulario';

/** Un rol que sirve para lo que se buscó, con el motivo por el que sirve. */
export interface Coincidencia {
  role: Role;
  /** Frases del estilo «dar de alta y editar Facturas de cliente». */
  motivos: string[];
  /** Cuántas capacidades coincidieron. Ordena el resultado. */
  puntaje: number;
}

/** Tope de motivos que se muestran por rol: más que esto deja de ser un resumen. */
const MAX_MOTIVOS = 4;

function coincide(terminos: string[], capacidad: RoleCapability, areaLabel: string): boolean {
  const heno = normalizarTexto(`${capacidad.label} ${capacidad.model} ${areaLabel}`);
  return terminos.some((termino) => heno.includes(termino));
}

/**
 * Roles que permiten hacer lo que dice la consulta.
 *
 * @returns `null` si la consulta está vacía (la pantalla muestra la lista completa),
 * o la lista de coincidencias ordenada por cuán bien encaja cada rol.
 */
export function buscarPorCapacidad(roles: Role[], consulta: string): Coincidencia[] | null {
  const terminos = expandirBusqueda(consulta);
  if (terminos.length === 0) return null;

  const coincidencias: Coincidencia[] = [];

  for (const role of roles) {
    const motivos: string[] = [];
    let puntaje = 0;

    for (const { area, capability } of capacidadesDe(role)) {
      if (!concedeAlgo(capability)) continue;
      if (!coincide(terminos, capability, etiquetaDeArea(area.key, area.label))) continue;
      puntaje += 1;
      // Se prioriza lo que deja HACER algo, no sólo mirar: quien busca «facturar»
      // quiere emitir facturas, no leerlas.
      if (capability.create || capability.write) puntaje += 1;
      if (motivos.length < MAX_MOTIVOS) {
        motivos.push(`${frasePermitida(capability)} · ${capability.label}`);
      }
    }

    if (puntaje > 0) coincidencias.push({ role, motivos, puntaje });
  }

  return coincidencias.sort((a, b) => b.puntaje - a.puntaje || a.role.name.localeCompare(b.role.name, 'es'));
}
